// Navigation sidebar component

import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';
import { router } from '../../lib/router.js';

/**
 * Navigation sidebar component
 * Shows main navigation items and staff section for admins
 */
class NavBar extends BaseComponent {
  static properties = {
    user: { type: Object },
    currentRoute: { type: String, attribute: 'current-route' },
  };

  static styles = css`
    :host {
      display: block;
    }

    .sidebar {
      display: flex;
      flex-direction: column;
      flex-shrink: 0;
      width: 250px;
      height: 100%;
      background-color: #212529;
      color: white;
    }

    .logo {
      padding: 1rem;
      border-bottom: 1px solid #495057;
    }

    .logo h4 {
      margin: 0;
      font-weight: 600;
    }

    nav {
      display: flex;
      flex-direction: column;
      flex: 1;
      padding: 0.5rem;
      overflow-y: auto;
      min-height: 0;
    }

    .nav-link {
      display: flex;
      align-items: center;
      padding: 0.5rem 1rem;
      color: white;
      text-decoration: none;
      border-radius: 0.375rem;
      transition: background-color 0.15s;
      cursor: pointer;
    }

    .nav-link:hover {
      background-color: rgba(255, 255, 255, 0.1);
    }

    .nav-link.active {
      background-color: #0d6efd;
    }

    .nav-link i {
      margin-right: 0.5rem;
      width: 20px;
      text-align: center;
    }

    .staff-section {
      margin-top: 0.5rem;
    }

    .staff-subnav {
      margin-left: 2rem;
    }

    .staff-subnav .nav-link {
      padding: 0.25rem 1rem;
      font-size: 0.875rem;
    }

    .user-section {
      padding: 1rem;
      border-top: 1px solid #495057;
    }

    .user-info {
      display: flex;
      align-items: center;
    }

    .user-avatar {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      margin-right: 0.5rem;
      object-fit: cover;
    }

    .user-avatar-placeholder {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background-color: #6c757d;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 0.5rem;
      font-weight: bold;
    }

    .user-details {
      flex: 1;
      font-size: 0.875rem;
    }

    .user-name {
      font-weight: 600;
    }

    .logout-btn {
      padding: 0.25rem 0.5rem;
      background: none;
      border: 1px solid #6c757d;
      border-radius: 0.25rem;
      color: white;
      cursor: pointer;
      transition: background-color 0.15s;
    }

    .logout-btn:hover {
      background-color: rgba(255, 255, 255, 0.1);
    }

    /* SVG Icons */
    .icon {
      width: 20px;
      height: 20px;
      margin-right: 0.5rem;
    }
  `;

  constructor() {
    super();
    /** @type {import('../../types/jsdoc-types.js').User|null} */
    this.user = null;
    this.currentRoute = '/';
  }

  get _navItems() {
    return [
      { id: 'adventures', label: 'Adventures', icon: 'map', path: '/adventures' },
      { id: 'games', label: 'My Games', icon: 'swords', path: '/games' },
      { id: 'characters', label: 'My Characters', icon: 'person', path: '/characters' },
      { id: 'friends', label: 'Friends', icon: 'people', path: '/friends' },
    ];
  }

  get _staffItems() {
    return [
      { id: 'staff-users', label: 'Users', icon: 'people', path: '/staff/users' },
      { id: 'staff-invitations', label: 'Invitations', icon: 'envelope', path: '/staff/invitations' },
      { id: 'staff-games', label: 'Games', icon: 'controller', path: '/staff/games' },
      { id: 'staff-adventures', label: 'Adventures', icon: 'map', path: '/staff/adventures' },
      { id: 'staff-tokens', label: 'Tokens', icon: 'person-circle', path: '/staff/tokens' },
      { id: 'staff-equipment', label: 'Equipment', icon: 'hammer', path: '/staff/equipment' },
      { id: 'staff-classes', label: 'Classes', icon: 'people', path: '/staff/classes' },
      { id: 'staff-feats', label: 'Feats', icon: 'stars', path: '/staff/feats' },
      { id: 'staff-backgrounds', label: 'Backgrounds', icon: 'person-vcard', path: '/staff/backgrounds' },
      { id: 'staff-species', label: 'Species', icon: 'globe', path: '/staff/species' },
      { id: 'staff-spells', label: 'Spells', icon: 'magic', path: '/staff/spells' },
      { id: 'staff-monsters', label: 'Monsters', icon: 'bug', path: '/staff/monsters' },
      { id: 'staff-paperdoll', label: 'Paper Doll', icon: 'layers', path: '/staff/paperdoll' },
    ];
  }

  get _isAdmin() {
    return this.user?.permissions?.includes('operate:rpgish');
  }

  get _userName() {
    return this.user?.username || this.user?.email || 'User';
  }

  get _userInitial() {
    return this._userName.charAt(0).toUpperCase();
  }

  get _isStaffRoute() {
    return this.currentRoute?.startsWith('/staff');
  }

  /** @param {string} path */
  _isActive(path) {
    if (path === '/games' && (this.currentRoute === '/' || this.currentRoute === '/games')) {
      return true;
    }
    return this.currentRoute?.startsWith(path);
  }

  /** @param {string} path */
  _navigate(path) {
    router.navigate(path);
  }

  _handleLogout() {
    this.dispatchEvent(new CustomEvent('logout', { bubbles: true }));
  }

  /** @param {string} icon */
  _renderIcon(icon) {
    // Use Bootstrap Icons for common icons
    return html`<i class="bi bi-${icon}"></i>`;
  }

  _renderSwordsIcon() {
    return html`
      <svg
        class="icon"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <polyline points="14.5 17.5 3 6 3 3 6 3 17.5 14.5"></polyline>
        <line x1="13" y1="19" x2="19" y2="13"></line>
        <line x1="16" y1="16" x2="20" y2="20"></line>
        <line x1="19" y1="21" x2="21" y2="19"></line>
        <polyline points="14.5 6.5 18 3 21 3 21 6 17.5 9.5"></polyline>
        <line x1="5" y1="14" x2="9" y2="18"></line>
        <line x1="7" y1="17" x2="4" y2="20"></line>
        <line x1="3" y1="19" x2="5" y2="21"></line>
      </svg>
    `;
  }

  render() {
    return html`
      <div class="sidebar">
        <!-- Logo -->
        <div class="logo">
          <h4>RPGish</h4>
        </div>

        <!-- Main navigation -->
        <nav>
          ${this._navItems.map(
            (item) => html`
              <a
                class="nav-link ${this._isActive(item.path) ? 'active' : ''}"
                @click="${() => this._navigate(item.path)}"
              >
                ${item.icon === 'swords' ? this._renderSwordsIcon() : this._renderIcon(item.icon)}
                <span>${item.label}</span>
              </a>
            `,
          )}

          <!-- Staff section (admin only) -->
          ${
            this._isAdmin
              ? html`
                <div class="staff-section">
                  <a
                    class="nav-link ${this._isStaffRoute ? 'active' : ''}"
                    @click="${() => this._navigate('/staff/users')}"
                  >
                    ${this._renderIcon('shield-lock')}
                    <span>Staff</span>
                  </a>
                  ${
                    this._isStaffRoute
                      ? html`
                        <nav class="staff-subnav">
                          ${this._staffItems.map(
                            (item) => html`
                              <a
                                class="nav-link ${this._isActive(item.path) ? 'active' : ''}"
                                @click="${() => this._navigate(item.path)}"
                              >
                                ${this._renderIcon(item.icon)}
                                <span>${item.label}</span>
                              </a>
                            `,
                          )}
                        </nav>
                      `
                      : ''
                  }
                </div>
              `
              : ''
          }
        </nav>

        <!-- User section -->
        ${
          this.user
            ? html`
              <div class="user-section">
                <div class="user-info">
                  ${
                    this.user.picture
                      ? html` <img class="user-avatar" src="${this.user.picture}" alt="Avatar" /> `
                      : html`
                        <div class="user-avatar-placeholder">
                          <span>${this._userInitial}</span>
                        </div>
                      `
                  }
                  <div class="user-details">
                    <div class="user-name">${this._userName}</div>
                  </div>
                  <button class="logout-btn" title="Logout" @click="${this._handleLogout}">
                    <i class="bi bi-box-arrow-right"></i>
                  </button>
                </div>
              </div>
            `
            : ''
        }
      </div>
    `;
  }
}

customElements.define('nav-bar', NavBar);
